import React, { useState } from 'react';
import {
    Grid,
    Typography,
    Box,
    TableContainer,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Divider,
    Link,
    Tooltip
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocationMap from 'components/organisms/locationMap/view';
import AmenitiesSection from './amenitiesSection'
import ResourcesSection from './resourcesSection';
import { InfoContainer, SectionTitle, InfoBox, ColumnContainer, MapContainer, LeftColumnContent, GridContainer, DescriptionTypography } from './styled';
import * as Icons from '@mui/icons-material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getTheme } from 'config/theme';

const GeneralInfo = ({ venue, slug, amenities, venueResources, loadingResources, bookingType, selectedDate, onBookTimeSlot, onDateChange, initialBookingType = null, onBookingTypeChange, onResourceModalClose, setSelectedResource, resourceToView }) => {
    const [theme,] = useState(getTheme(null));
    const googleMapsLink = `https://www.google.com/maps/search/?api=1&query=${venue?.location?.[0]},${venue?.location?.[1]}`;
    const getDayName = (day) => {
        switch (day) {
            case 0:
                return 'Monday'
            case 1:
                return 'Tuesday'
            case 2:
                return 'Wednesday'
            case 3:
                return 'Thursday'
            case 4:
                return 'Friday'
            case 5:
                return 'Saturday'
            case 6:
                return 'Sunday'
            default:
                return ''
        }
    }

    const getIconComponent = (iconName) => {
        return Icons[iconName] || Icons.Help; // Default to HelpIcon if not found
    };

    const handleModalClosed = () => {
        // Call the parent's handler to reset the selectedResource
        if (onResourceModalClose) {
            onResourceModalClose();
        }
    };

    return (
        <InfoContainer elevation={3}>
            <GridContainer container spacing={2}>
                {/* Left Column */}
                <Grid item xs={12} md={12}>
                    <DescriptionTypography variant="body2">
                        {venue.short_description}
                    </DescriptionTypography>
                    <AmenitiesSection
                        amenities={amenities}
                        getIconComponent={getIconComponent}
                        theme={theme}
                    />
                    <Divider sx={{ my: 2, display: { xs: 'none', md: 'block' } }} />

                </Grid>
                {/* Add Resources Section here */}
                {venue.floors && venue.floors.length > 0 && (
                    <Grid item xs={12} md={12}>
                        <SectionTitle>Catalogue</SectionTitle>
                        <div id="resources-section">
                            <ResourcesSection
                                floors={venue?.floors}
                                venueSlug={slug}
                                selectedDate={selectedDate}
                                venueResources={venueResources} // Pass the API response if you already fetched it
                                onBookTimeSlot={onBookTimeSlot}
                                loadingResources={loadingResources}
                                onDateChange={onDateChange}
                                initialBookingType={initialBookingType}
                                onBookingTypeChange={onBookingTypeChange}
                                onModalClose={handleModalClosed}
                                resourceToView={resourceToView}
                                onSelectResource={(resource) => {
                                    // First, determine if it's from BookingCreator or ResourcesSection
                                    setSelectedResource(resource);
                                }}

                            />
                        </div>
                        <Divider sx={{ my: 2, display: { xs: 'none', md: 'block' } }} />
                    </Grid>
                )}
                <ColumnContainer item xs={12} md={6}>
                    <LeftColumnContent>
                        <SectionTitle>Location</SectionTitle>
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <MapContainer>
                                {venue?.location?.[0] && venue?.location?.[1] && (
                                    <LocationMap
                                        latitude={venue.location[0]}
                                        longitude={venue.location[1]}
                                        name={venue.name}
                                        address={venue.single_address}
                                        googleMapsUrl={venue.google_maps_url}
                                        height="100%"
                                        showButton={false}
                                    />
                                )}
                            </MapContainer>

                            <InfoBox
                                sx={{
                                    mt: 1,
                                    cursor: googleMapsLink ? 'pointer' : 'default',
                                    textDecoration: 'none',
                                    '&:hover': googleMapsLink ? {
                                        backgroundColor: 'primary.hover',
                                        borderRadius: '4px',
                                    } : {}
                                }}
                                component={googleMapsLink ? 'a' : 'div'}
                                href={googleMapsLink}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <LocationOnIcon color="primary" />
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    justifyContent: 'space-between'
                                }}>
                                    <Typography variant="body2" sx={{ color: 'primary.main' }}>
                                        {venue?.single_address}
                                    </Typography>

                                    {googleMapsLink && (
                                        <Tooltip title="Open in Google Maps">
                                            <OpenInNewIcon
                                                fontSize="small"
                                                color="primary"
                                                sx={{
                                                    ml: 1,
                                                    fontSize: '16px',
                                                    opacity: 0.7
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                </Box>
                            </InfoBox>
                        </Box>
                    </LeftColumnContent>
                </ColumnContainer>

                {/* Right Column */}
                <Grid item xs={12} md={6}>
                    <SectionTitle>Opening Times</SectionTitle>
                    <TableContainer>
                        <Table size="small">
                            <TableBody>
                                {[...Array(7).keys()].map(day => {
                                    const openTime = venue?.open_time?.find(o => o.day === day);
                                    const formattedOpen = openTime?.time_start?.split(':').slice(0, 2).join(':') || 'Closed';
                                    const formattedClose = openTime?.time_end?.split(':').slice(0, 2).join(':') || '';
                                    return (
                                        <TableRow key={day}>
                                            <TableCell>{getDayName(day)}</TableCell>
                                            <TableCell>{formattedOpen} {formattedClose && `- ${formattedClose}`}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <div dangerouslySetInnerHTML={{ __html: venue?.access }} />
                </Grid>
            </GridContainer>

            <Divider sx={{ my: 2, display: { xs: 'none', md: 'block' } }} />
            <SectionTitle sx={{ mt: { xs: 3 } }}>About us</SectionTitle>
            <Box component="div" sx={{ typography: 'body2' }}>
                <div dangerouslySetInnerHTML={{ __html: venue?.long_description }} />
            </Box>
        </InfoContainer>
    );
};

export default GeneralInfo;